import { AInfoSectionItem } from "@atiautomacao/ati-ui-library";
import { TeleObjectBessData } from "../Powerplant/batteries/BatteryReactQuery";

const NO_DATA = "-";
const DEFAULT_MAX_DECIMALS = 3;

// shortNames dos tele_object_data_type consumidos pelos cards (espelham o enum BessTeleObjectDataType
// no main-api). As fases AC (V_AC_AB..A_AC_C) foram criadas na SGD-3735.
const SHORT_NAME = {
    FREQUENCY: "FREQ",
    TEMPERATURE: "TEMP",
    CYCLE_COUNT: "CICLOS",
    REACTIVE_POWER: "POT_REAT",
    APPARENT_POWER: "POT_AP",
    POWER_FACTOR: "FP",
    AC_VOLTAGE_AB: "V_AC_AB",
    AC_VOLTAGE_BC: "V_AC_BC",
    AC_VOLTAGE_CA: "V_AC_CA",
    DC_VOLTAGE: "V_DC",
    AC_CURRENT_A: "A_AC_A",
    AC_CURRENT_B: "A_AC_B",
    AC_CURRENT_C: "A_AC_C",
    DC_CURRENT: "A_DC",
} as const;

export interface BessInfoCard {
    title: string;
    items: AInfoSectionItem[];
}

interface RowSpec {
    label: string;
    shortName: string;
    // Unidade usada quando o teleobjeto não tem unity configurada (unity_id NULL). A do banco tem prioridade.
    defaultUnit?: string;
    // Máximo de casas decimais (default 3). Por padrão remove zeros à direita: 127.00 -> "127", 127.56 -> "127.56".
    decimals?: number;
    // Mantém exatamente `decimals` casas, sem remover zeros (ex.: Frequência -> "60.000").
    fixedDecimals?: boolean;
}

const formatNumber = (value: number, maxDecimals: number, fixedDecimals: boolean): string => {
    const rounded = value.toFixed(maxDecimals);
    // fixedDecimals mantém "60.000"; caso contrário, remove os zeros à direita ("127.00" -> "127").
    return fixedDecimals ? rounded : parseFloat(rounded).toString();
};

// RN5d: mesmo quando o valor é "-" (ausente/desatualizado), a unidade permanece visível.
const buildValue = (value: number | null | undefined, unity: string, spec: RowSpec): string => {
    const unit = unity || spec.defaultUnit || "";
    const unitSuffix = unit ? ` ${unit}` : "";
    const formatted = value === null || value === undefined
        ? NO_DATA
        : formatNumber(value, spec.decimals ?? DEFAULT_MAX_DECIMALS, spec.fixedDecimals ?? false);
    return `${formatted}${unitSuffix}`;
};

const item = (spec: RowSpec, bessData: TeleObjectBessData[]): AInfoSectionItem => {
    const found = bessData.find((data) => data.shortName === spec.shortName);
    return { key: spec.shortName, label: spec.label, value: buildValue(found?.tmValue ?? null, found?.unityName ?? "", spec) };
};

// SGD-3735: monta os 4 cards de Informações Instantâneas na ordem definida no layout.
export const buildBessInstantInfoCards = (bessData: TeleObjectBessData[]): BessInfoCard[] => [
    {
        title: "Dados de Funcionamento",
        items: [
            item({ label: "Frequência", shortName: SHORT_NAME.FREQUENCY, defaultUnit: "Hz", decimals: 3, fixedDecimals: true }, bessData),
            item({ label: "Temperatura", shortName: SHORT_NAME.TEMPERATURE, defaultUnit: "ºC", decimals: 2 }, bessData),
            item({ label: "Ciclos", shortName: SHORT_NAME.CYCLE_COUNT }, bessData),
        ],
    },
    {
        title: "Potência",
        items: [
            item({ label: "Reativa", shortName: SHORT_NAME.REACTIVE_POWER, defaultUnit: "kVAr" }, bessData),
            item({ label: "Aparente", shortName: SHORT_NAME.APPARENT_POWER, defaultUnit: "kVA" }, bessData),
            item({ label: "Fator", shortName: SHORT_NAME.POWER_FACTOR }, bessData),
        ],
    },
    {
        title: "Tensão AC | DC",
        items: [
            item({ label: "AB", shortName: SHORT_NAME.AC_VOLTAGE_AB, defaultUnit: "V", decimals: 2 }, bessData),
            item({ label: "BC", shortName: SHORT_NAME.AC_VOLTAGE_BC, defaultUnit: "V", decimals: 2 }, bessData),
            item({ label: "CA", shortName: SHORT_NAME.AC_VOLTAGE_CA, defaultUnit: "V", decimals: 2 }, bessData),
            item({ label: "DC", shortName: SHORT_NAME.DC_VOLTAGE, defaultUnit: "V", decimals: 2 }, bessData),
        ],
    },
    {
        title: "Corrente AC | DC",
        items: [
            item({ label: "A", shortName: SHORT_NAME.AC_CURRENT_A, defaultUnit: "A", decimals: 2 }, bessData),
            item({ label: "B", shortName: SHORT_NAME.AC_CURRENT_B, defaultUnit: "A", decimals: 2 }, bessData),
            item({ label: "C", shortName: SHORT_NAME.AC_CURRENT_C, defaultUnit: "A", decimals: 2 }, bessData),
            item({ label: "DC", shortName: SHORT_NAME.DC_CURRENT, defaultUnit: "A", decimals: 2 }, bessData),
        ],
    },
];
